import React from 'react';
import { BackgroundColor } from '@extensions/AttributePanel/components/attributes/BackgroundColor';
import {
	ImageUploaderField,
	InputWithUnitField,
	SelectField,
} from '@extensions/components/Form';
import { Width } from '@extensions/AttributePanel/components/attributes/Width';
import { Height } from '@extensions/AttributePanel/components/attributes/Height';
import { VerticalAlign } from '@extensions/AttributePanel/components/attributes/VerticalAlign';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { Collapse, Grid } from '@arco-design/web-react';
import { useEditorProps, useFocusIdx } from '@bay-email/editor';
import {
	BsAlignBottom,
	BsAlignCenter,
	BsAlignEnd,
	BsAlignMiddle,
	BsAlignStart,
	BsAlignTop,
} from 'react-icons/bs';
//import { AttributesPanelWrapper } from '@extensions/AttributePanel/components/attributes/AttributesPanelWrapper';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { t } from '@bay-email/core';
import { pixelAdapter, imageHeightAdapter } from '../../adapter';

const overlayModeOptions = [
	{
		value: 'none',
		get label() {
			return t('None');
		},
	},
	{
		value: 'dark',
		get label() {
			return t('Dark Vignette');
		},
	},
	{
		value: 'light',
		get label() {
			return t('Light Vignette');
		},
	},
];

const horizontalPositionIcons = {
	left: BsAlignStart,
	center: BsAlignCenter,
	right: BsAlignEnd,
};

const verticalPositionIcons = {
	top: BsAlignTop,
	center: BsAlignMiddle,
	bottom: BsAlignBottom,
};

const backgroundPositionOptions = [
	'left top',
	'left center',
	'left bottom',
	'center top',
	'center center',
	'center bottom',
	'right top',
	'right center',
	'right bottom',
].map(value => {
	const [horizontalPosition, verticalPosition] = value.split(' ') as [
		keyof typeof horizontalPositionIcons,
		keyof typeof verticalPositionIcons,
	];
	const HorizontalIcon = horizontalPositionIcons[horizontalPosition];
	const VerticalIcon = verticalPositionIcons[verticalPosition];

	return {
		value,
		label: (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 4,
						color: 'var(--color-text-2)',
					}}
				>
					<HorizontalIcon size={14} />
					<VerticalIcon size={14} />
				</span>
				<span>
					{value
						.split(' ')
						.map(part => part.charAt(0).toUpperCase() + part.slice(1))
						.join(' ')}
				</span>
			</span>
		),
	};
});

export function Hero() {
	const { focusIdx } = useFocusIdx();
	const { onUploadImage } = useEditorProps();

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={['0', '1', '4']}
			style={{ padding: 0 }}
		>
			<Collapse.Item
				name='0'
				header={t('Content Block Settings')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					<ImageUploaderField
						labelHidden
						headerTitle={t('Background Image')}
						name={`${focusIdx}.attributes.background-url`}
						uploadHandler={onUploadImage}
						context='image-src'
						isImageCropperAllowed={true}
						showLink={false}
						formItem={{ className: 'mb-2' }}
					/>
					<div
						style={{
							marginTop: '16px',
							paddingTop: '16px',
							borderTop: '1px solid var(--color-border-2)',
						}}
					>
						<SelectField
							label={t('Position')}
							name={`${focusIdx}.attributes.background-position`}
							options={backgroundPositionOptions}
						/>
					</div>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='1'
				header={t('Dimension')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<Height config={imageHeightAdapter} />
							</div>
						</Grid.Col>
						<Grid.Col span={12}>
							<div style={{ paddingLeft: '8px' }}>
								<Width
									autoEnabled
									config={pixelAdapter}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<Padding
						title={t('Inner Padding')}
						showResetAll
					/>
					<VerticalAlign />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='4'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<InputWithUnitField
									label={`${t('Border radius')} (px)`}
									name={`${focusIdx}.attributes.border-radius`}
									config={pixelAdapter}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<BackgroundColor />
					<SelectField
						label={t('Vignette Overlay')}
						name={`${focusIdx}.data.value.overlayMode`}
						options={overlayModeOptions}
					/>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='5'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
